// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Structured logger — wraps pino with:
 *   - browser.write hook in actual browsers; custom stream in Node.js/Vitest
 *   - window.__pinoLogs capture for Playwright/devtools inspection
 *   - Automatic context binding (from bindContext())
 *   - Automatic OTEL trace_id/span_id injection
 *   - PII sanitization
 *   - message fallback: if message is empty, defaults to obj.event
 *
 * Mirrors Python provide.telemetry get_logger().
 */
import pino from 'pino';
import { configFromEnv, getConfig, _getConfigVersion } from './config';
import { getContext } from './context';
import { shouldAllow } from './consent';
import { computeErrorFingerprint } from './fingerprint';
import { formatPretty, supportsColor } from './pretty';
import { _emittedField, _incrementHealth } from './health';
import { emitLogRecord } from './otel-logs';
import { sanitize, sanitizePayload } from './pii';
import { EventSchemaError, validateEventName, validateRequiredKeys } from './schema';
import { tryAcquire, release } from './backpressure';
import { setSamplingPolicy, shouldSample } from './sampling';
import { getTraceContext } from './tracing';
/** Pino level number → console method name. */
const LEVEL_MAP = {
    10: 'trace',
    20: 'debug',
    30: 'log',
    40: 'warn',
    50: 'error',
    60: 'error',
};
/** Pino level number → semantic level string for consent checks. */
const CONSENT_LEVEL_MAP = {
    10: 'trace',
    20: 'debug',
    30: 'info',
    40: 'warn',
    50: 'error',
    60: 'error',
};
// Pino root instance — lazily created so config is read after setupTelemetry().
let _root = null;
let _rootConfigVersion = -1;
function resolveLoggerConfig() {
    // Before setupTelemetry() runs, logger lazy-init should still honor env config.
    return _getConfigVersion() === 0 ? configFromEnv() : getConfig();
}
function applyLazyLoggerPoliciesFromEnv() {
    if (_getConfigVersion() !== 0)
        return;
    const cfg = configFromEnv();
    setSamplingPolicy('logs', { defaultRate: cfg.samplingLogsRate });
}
/**
 * Build the write hook that enriches, sanitizes, captures, and optionally
 * emits each log record.  Config is read dynamically on every invocation so
 * that resetTelemetryState() + setupTelemetry() changes take effect without
 * needing to rebuild the hook closure.
 */
export function makeWriteHook() {
    // pino's WriteFn signature uses `object`; we cast internally for safe property access.
    return (obj) => {
        // Read config dynamically — avoids stale-capture bug after _resetConfig().
        const cfg = resolveLoggerConfig();
        const o = obj;
        // Consent gate: drop records the current consent level forbids.
        const levelLabel = CONSENT_LEVEL_MAP[o['level']] ?? 'info';
        if (!shouldAllow('logs', levelLabel))
            return;
        // Sampling gate: probabilistically drop records based on configured rate.
        // Pass the canonical event key so per-event override rates take effect.
        const samplingKey = String(o['event'] ?? o['message'] ?? '');
        if (!shouldSample('logs', samplingKey))
            return;
        // Backpressure gate: drop when the log queue is full.
        const ticket = tryAcquire('logs');
        if (!ticket)
            return;
        try {
            // Merge module-level context bindings first, then overlay trace context
            // so real trace/span IDs always win over user-bound values.
            Object.assign(o, getContext());
            const ids = getTraceContext();
            if (ids.trace_id)
                o['trace_id'] = ids.trace_id;
            if (ids.span_id)
                o['span_id'] = ids.span_id;
            // Ensure message is always non-empty — pino sets message='' when no string arg is passed.
            if (!o['message'])
                o['message'] = o['event'] ?? '';
            // Caller info injection — intentionally expensive (creates Error per call).
            // Stryker disable all
            if (cfg.logIncludeCaller) {
                const err = new Error();
                const stack = err.stack?.split('\n');
                /* v8 ignore next -- stack is always defined in V8 */
                if (stack) {
                    for (const frame of stack.slice(1)) {
                        if (!frame.includes('logger.ts') &&
                            !frame.includes('node_modules') &&
                            !frame.includes('pino')) {
                            const match = frame.match(/\((.+):(\d+):\d+\)/) ?? frame.match(/at (.+):(\d+):\d+/);
                            /* v8 ignore next -- match always succeeds for V8 stack frames */
                            if (match) {
                                o['caller_file'] = match[1].replace(/^.*\//, ''); // basename only
                                o['caller_line'] = Number(match[2]);
                            }
                            break;
                        }
                    }
                }
            }
            // Stryker enable all
            // Error fingerprinting — stable hash from error name + stack.
            const errObj = o['err'];
            const excName = (o['exc_name'] ?? o['exception'] ?? errObj?.['type'] ?? errObj?.['name']);
            if (excName) {
                const stack = (errObj?.['stack'] ?? o['stack']);
                o['error_fingerprint'] = computeErrorFingerprint(String(excName), stack);
            }
            // PII sanitization: blocked keys + secret detection + custom PII rules.
            if (cfg.logSanitize) {
                sanitize(o, cfg.sanitizeFields);
                sanitizePayload(o, [], { maxDepth: cfg.piiMaxDepth });
            }
            // Strip timestamp when configured off.
            if (!cfg.logIncludeTimestamp) {
                delete o['time'];
            }
            // Schema validation — annotate instead of dropping.
            // Preserves telemetry while flagging violations via _schema_error.
            // Cross-language standard (Python/Rust/Go match).
            if (cfg.requiredLogKeys.length > 0) {
                try {
                    validateRequiredKeys(o, cfg.requiredLogKeys);
                }
                catch (e) {
                    if (e instanceof EventSchemaError) {
                        o['_schema_error'] = e.message;
                    }
                    else {
                        throw e;
                    }
                }
            }
            /* v8 ignore next -- V8 cannot fully attribute all ?? branches in a single expression */
            if (cfg.strictSchema || cfg.strictEventName) {
                const event = String(o['event'] ?? o['message'] ?? '');
                if (event) {
                    try {
                        validateEventName(event);
                    }
                    catch (e) {
                        if (e instanceof EventSchemaError) {
                            o['_schema_error'] = e.message;
                        }
                        else {
                            throw e;
                        }
                    }
                }
            }
            // Count every record that survives all filters as emitted.
            _incrementHealth(_emittedField('logs'));
            // Export to OTLP when a log provider is registered (noop otherwise).
            emitLogRecord(o);
            // Capture to window.__pinoLogs for Playwright and devtools inspection.
            // Check is done inline (not at module load) so it works when loaded in Node.js
            // test environments that later gain a jsdom window.
            if (typeof window !== 'undefined' && cfg.captureToWindow) {
                if (!('__pinoLogs' in window)) {
                    window['__pinoLogs'] = [];
                }
                window['__pinoLogs'].push(o);
            }
            // Emit to console only when explicitly enabled (opt-in).
            if (cfg.consoleOutput) {
                const method = LEVEL_MAP[o['level']] ?? 'log';
                if (cfg.logFormat === 'pretty' || cfg.logFormat === 'console') {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    console[method](formatPretty(o, supportsColor(), {
                        keyColor: cfg.logPrettyKeyColor,
                        valueColor: cfg.logPrettyValueColor,
                        fields: cfg.logPrettyFields,
                    }));
                }
                else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    console[method](JSON.stringify(o));
                }
            }
        }
        finally {
            release(ticket);
        }
    };
}
function getRootLogger() {
    const currentVersion = _getConfigVersion();
    // Stryker disable next-line ConditionalExpression
    if (_root && _rootConfigVersion === currentVersion)
        return _root;
    _root = null;
    _rootConfigVersion = currentVersion;
    const cfg = resolveLoggerConfig();
    applyLazyLoggerPoliciesFromEnv();
    const hook = makeWriteHook();
    // pino only invokes browser.write when process.version is absent (real browser).
    // In Node.js / Vitest, we use a custom destination stream that forwards every
    // serialised log line back through the write hook.
    // Stryker disable all
    const isNodeEnv = typeof process !== 'undefined' && typeof process.version === 'string';
    /* c8 ignore else */
    if (isNodeEnv) {
        const stream = {
            write(msg) {
                try {
                    hook(JSON.parse(msg.trimEnd()));
                }
                catch {
                    // Ignore malformed lines (e.g. pino flush sentinels).
                }
            },
        };
        _root = pino({
            base: { service: cfg.serviceName, env: cfg.environment, version: cfg.version },
            level: cfg.logLevel,
            messageKey: 'message',
        }, stream);
    }
    else {
        /* c8 ignore next 9 */
        _root = pino({
            base: { service: cfg.serviceName, env: cfg.environment, version: cfg.version },
            level: cfg.logLevel,
            messageKey: 'message',
            browser: {
                write: hook,
            },
        });
    }
    // Stryker enable all
    return _root;
}
function adaptPino(pinoLogger) {
    // Stryker disable all
    return {
        trace: (obj, msg) => pinoLogger.trace(obj, msg ?? ''),
        debug: (obj, msg) => pinoLogger.debug(obj, msg ?? ''),
        info: (obj, msg) => pinoLogger.info(obj, msg ?? ''),
        warn: (obj, msg) => pinoLogger.warn(obj, msg ?? ''),
        error: (obj, msg) => pinoLogger.error(obj, msg ?? ''),
        child: (bindings) => adaptPino(pinoLogger.child(bindings)),
    };
    // Stryker enable all
}
/**
 * Find the longest-prefix match in logModuleLevels for the given logger name.
 * Returns the matched level string, or undefined if no match.
 * Mirrors Python _LevelFilter longest-prefix matching.
 */
function findModuleLevel(name, moduleLevels) {
    let bestMatch;
    let bestLen = -1;
    for (const prefix of Object.keys(moduleLevels)) {
        if ((prefix === '' || name === prefix || name.startsWith(prefix + '.')) &&
            prefix.length > bestLen) {
            bestMatch = prefix;
            bestLen = prefix.length;
        }
    }
    return bestMatch !== undefined ? moduleLevels[bestMatch] : undefined;
}
/**
 * Return a logger for the given name.
 * Name appears as the `name` field in every log record.
 * Mirrors Python: get_logger(name)
 */
export function getLogger(name) {
    const root = getRootLogger();
    // Stryker disable next-line ObjectLiteral
    const pinoLogger = name ? root.child({ name }) : root;
    // Apply per-module level overrides (longest-prefix match).
    if (name) {
        const cfg = getConfig();
        const moduleLevels = cfg.logModuleLevels;
        if (Object.keys(moduleLevels).length > 0) {
            const level = findModuleLevel(name, moduleLevels);
            if (level) {
                pinoLogger.level = level.toLowerCase();
            }
        }
    }
    return adaptPino(pinoLogger);
}
/** Reset the root logger (forces re-creation with current config on next call). */
// Stryker disable next-line BlockStatement
export function _resetRootLogger() {
    _root = null;
    _rootConfigVersion = -1;
}
/** Module-level lazy singleton logger. Mirrors Python: logger = get_logger('default'). */
// Stryker disable all
export const logger = {
    trace: (obj, msg) => getLogger('default').trace(obj, msg),
    debug: (obj, msg) => getLogger('default').debug(obj, msg),
    info: (obj, msg) => getLogger('default').info(obj, msg),
    warn: (obj, msg) => getLogger('default').warn(obj, msg),
    error: (obj, msg) => getLogger('default').error(obj, msg),
    child: (bindings) => getLogger('default').child(bindings),
};
// Stryker enable all
